/**
 * <s-reel-coverflow> lays out [data-reel] cards as a 3D-ish coverflow: the active reel
 * is large and in front, neighbours shrink, fade and tuck behind it on each side.
 * [data-reel-prev] / [data-reel-next] buttons and swipe move between reels; clicking a
 * side reel brings it to the front (clicking the front reel follows its link).
 * Only the front reel's video plays; the rest pause to save data. Shoppers who prefer
 * reduced motion get instant moves.
 */
class ReelCoverflow extends HTMLElement {
  #ac;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    const cards = this.#cards();
    if (!cards.length) return;
    this.#index = Math.floor((cards.length - 1) / 2);

    this.querySelector('[data-reel-prev]')?.addEventListener('click', () => this.go(this.#index - 1), { signal });
    this.querySelector('[data-reel-next]')?.addEventListener('click', () => this.go(this.#index + 1), { signal });

    cards.forEach((card, i) => {
      card.addEventListener(
        'click',
        (event) => {
          if (i !== this.#index) {
            event.preventDefault();
            this.go(i);
          }
        },
        { signal }
      );
    });

    let startX = 0;
    let startY = 0;
    this.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; startY = e.touches[0].clientY; }, { passive: true, signal });
    this.addEventListener(
      'touchend',
      (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.#index + (dx < 0 ? 1 : -1));
      },
      { passive: true, signal }
    );
    this.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'ArrowLeft') this.go(this.#index - 1);
        if (e.key === 'ArrowRight') this.go(this.#index + 1);
      },
      { signal }
    );

    window.addEventListener('resize', () => this.#layout(), { passive: true, signal });

    const io = new IntersectionObserver(([entry]) => {
      this.toggleAttribute('data-visible', entry.isIntersecting);
      this.#playActive();
    });
    io.observe(this);
    signal.addEventListener('abort', () => io.disconnect());

    this.#layout();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #cards() {
    return [...this.querySelectorAll('[data-reel]')];
  }

  go(i) {
    const count = this.#cards().length;
    this.#index = (i + count) % count;
    this.#layout();
  }

  #layout() {
    const cards = this.#cards();
    const count = cards.length;
    const wide = window.matchMedia('(min-width: 768px)').matches;
    const step = wide ? 0.62 : 0.55;
    cards.forEach((card, i) => {
      let offset = i - this.#index;
      if (offset > count / 2) offset -= count;
      if (offset < -count / 2) offset += count;
      const abs = Math.abs(offset);
      const scale = abs === 0 ? 1 : Math.max(0.6, 0.8 - (abs - 1) * 0.12);
      const shift = offset === 0 ? 0 : Math.sign(offset) * (step + (abs - 1) * 0.3) * 100;
      card.style.transform = `translate(-50%, -50%) translateX(${shift}%) scale(${scale})`;
      card.style.zIndex = String(10 - abs);
      card.style.opacity = abs > 2 ? '0' : abs === 2 ? '0.55' : abs === 1 ? '0.85' : '1';
      card.style.pointerEvents = abs > 2 ? 'none' : 'auto';
      card.toggleAttribute('data-active', abs === 0);
      card.querySelector('a')?.setAttribute('tabindex', abs === 0 ? '0' : '-1');
      card.setAttribute('aria-hidden', abs === 0 ? 'false' : 'true');
    });
    this.#playActive();
  }

  #playActive() {
    const visible = this.hasAttribute('data-visible');
    this.#cards().forEach((card, i) => {
      const video = card.querySelector('video');
      if (!video) return;
      if (visible && i === this.#index) {
        video.play?.().catch(() => {});
      } else {
        video.pause?.();
      }
    });
  }
}

if (!customElements.get('s-reel-coverflow')) customElements.define('s-reel-coverflow', ReelCoverflow);
